import http from "@/helper/http";
import {BaseVO, GetByIdBO, PaginationBO, PaginationVO} from "@/model/global";
import {useMutation, useQuery} from "@tanstack/react-query";
import {ReviewStatusVO} from "@/service/instance.s";
import {ContractPO} from "@/model/customer";
import {SearchBO} from "@/service/common.s";
import {ProductPO} from "@/model/product";
import {FilePO} from "@/model/content";
import {WriteContractForm} from "@/pages/Contract/WriteContract";
import {AssignItemsForm} from "@/widget/ProcessApproval/AssignProduct";
import {InvoicePO, PaybackPO} from "@/model/finance";

export enum ContractEnum {
    GetContractPage = "/contract/getContractPage",
    GetContract = "/contract/getContract",
    CreateContract = "/contract/createContract",
    SearchContract = "/contract/searchContract",
    SearchProduct = "/contract/searchProduct",
    UpdateContract = "/contract/updateContract",
    AssignProduct = "/contract/assignProduct",
    TransferProduct = "/contract/transferProduct",
    GetUnassignedPage = "/contract/getUnassignedPage",
    ClaimProduct = "/contract/claimProduct",
}

export interface ContractPageBO {
    keyword?: string;
}

export interface ContractPageVO extends ReviewStatusVO, ContractPO {
    location: string
}

export interface SearchContractVO {
    id: number
    auditStatus: number
    contractName: string
    productNum: number
    customerId: number
    totalPrice: string
    products: ProductPO[]
    productTypeIds: string
}

export interface ContractVO extends ContractPO {
    annexes: FilePO[]
    originals: FilePO[]
    products: ProductPO[]
    paybacks: PaybackPO[]
    invoices: InvoicePO[]
}

export interface AssignProductBO {
    businessId: number
    executeId: string
    instanceId: number
    productTypeId: number
    assignItems: AssignItemsForm
}

export interface TransferProductBO {
    productTypeId: number
    writerId: number
    writer: string
    ids: number[]
}

export interface ClaimProductBO {
    ids: number[]
    productTypeId: number
}

export interface UnassignedBO extends PaginationBO {
    handleType: number
}

export interface UnassignedVO extends ReviewStatusVO {
    id: number;
    creator: string;
    customerName: string;
    contractName: string;
    kinds: string;
    TotalPrice: string
    dealTime: Date;
    contractNum: string;
    seller: string
    sellerId: number;
    totalPrice: string;
    instanceId: number
}

export interface SearchProductBO extends GetByIdBO {
    productTypeIds?: number
}

export const useQueryContractPage = () => useMutation({
    mutationFn: (data: ContractPageBO) => http.post<BaseVO<PaginationVO<ContractPageVO>>>({
        url: ContractEnum.GetContractPage,
        data: data,
    }),
})

export const useUnassignedPage = () => useMutation({
    mutationFn: (data: UnassignedBO) => http.post<BaseVO<PaginationVO<UnassignedVO>>>({
        url: ContractEnum.GetUnassignedPage,
        data: data,
    }),
})

export const useAssignProduct = () => useMutation({
    mutationFn: (data: AssignProductBO) => http.post<BaseVO<null>>({
        url: ContractEnum.AssignProduct,
        data
    }),
})

export const useTransferProduct = () => useMutation({
    mutationFn: (data: TransferProductBO) => http.post<BaseVO<null>>({
        url: ContractEnum.TransferProduct,
        data
    }),
})

export const useClaimProduct = () => useMutation({
    mutationFn: (data: ClaimProductBO) => http.post<BaseVO<null>>({
        url: ContractEnum.ClaimProduct,
        data
    }),
})

export const useSearchContract = () => useMutation({
    mutationFn: (data: SearchBO) => http.post<BaseVO<SearchContractVO[]>>({
        url: ContractEnum.SearchContract,
        data: data
    })
})

export const useCreateContract = () => useMutation({
    mutationFn: (data: WriteContractForm) => http.put<BaseVO<null>>({url: ContractEnum.CreateContract, data}),
})

export const useUpdateContract = () => useMutation({
    mutationFn: (data: WriteContractForm) => http.post<BaseVO<null>>({url: ContractEnum.UpdateContract, data}),
})

export const useQueryContract = (params: GetByIdBO, enabled = true) => useQuery<BaseVO<ContractVO>>({
    queryKey: [params.id], queryFn: () => http.get<BaseVO<ContractVO>>({
        url: ContractEnum.GetContract,
        params: params
    }),
    enabled
})

export const useSearchProduct = () => useMutation({
    mutationFn: (params: SearchProductBO) => http.get<BaseVO<ProductPO[]>>({
        url: ContractEnum.SearchProduct,
        params: params
    }),
})


